/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useState } from 'react';
import { Seconds } from '@wit/vtv-x-foundation-lib';
import { useDispatch, useSelector } from 'react-redux';
import { Selectors, ActionCreators } from 'store/ducks/visualScrubber';
import VisualScrubberUtils from 'services/utils/VisualScrubberUtils';
import { StoreState } from 'store/store';
import SideThumbnails, { Side } from './SideThumbnails';
import Thumbnail from './Thumbnail';

interface Props {
  progress: Seconds;
  liveProgress: Seconds;
  inLivePosition: boolean;
}

export default function VisualScrubber({ progress, liveProgress, inLivePosition }: Props) {
  const dispatch = useDispatch();

  const [toRetryOnError, setToRetryOnError] = useState(false);

  const currentPositions = VisualScrubberUtils.getPositionsFromProgress(progress);

  const prevSpriteUrl = useSelector((state: StoreState) =>
    Selectors.sprites.getSpriteUrl(state, currentPositions.spriteIndex - 1)
  );
  const mainSpriteUrl = useSelector((state: StoreState) =>
    Selectors.sprites.getSpriteUrl(state, currentPositions.spriteIndex)
  );
  const nextSpriteUrl = useSelector((state: StoreState) =>
    !inLivePosition ? Selectors.sprites.getSpriteUrl(state, currentPositions.spriteIndex + 1) : ''
  );

  const mainSpriteError = useSelector((state: StoreState) =>
    Selectors.sprites.getError(state, currentPositions.spriteIndex)
  );

  useEffect(() => {
    if (currentPositions.spriteIndex >= 0) {
      dispatch(ActionCreators.sprites.requestSprite(progress, liveProgress, currentPositions));
    }
  }, [dispatch, progress, currentPositions, liveProgress]);

  useEffect(() => {
    if (mainSpriteError || toRetryOnError) {
      dispatch(ActionCreators.streamWatchers.retry());
    }
  }, [dispatch, mainSpriteError, toRetryOnError]);

  return (
    <div className="visual-scrubber-container" id="VisualScrubber">
      <div className="vs-sprites-container">
        <SideThumbnails
          side={Side.LEFT}
          spriteUrl={prevSpriteUrl}
          mainSpriteUrl={mainSpriteUrl}
          mainThumbnailIndex={currentPositions.thumbnailIndex}
          isInvisible={currentPositions.spriteIndex === 0 && currentPositions.thumbnailIndex < 2}
        />

        <Thumbnail
          isMain
          spriteUrl={mainSpriteUrl}
          index={currentPositions.thumbnailIndex}
          onError={() => !toRetryOnError && setToRetryOnError(true)}
        />

        <SideThumbnails
          side={Side.RIGHT}
          spriteUrl={nextSpriteUrl}
          mainSpriteUrl={!inLivePosition ? mainSpriteUrl : undefined}
          mainThumbnailIndex={currentPositions.thumbnailIndex}
        />
      </div>
    </div>
  );
}
